// ── Parsing helpers ─────────────────────────────────────────────────
const STATIC_RE = /^(?:'[^']*'|"[^"]*"|-?\d+(?:\.\d+)?|true|false|null)$/;
const BATCH_RE = /^(BATCH_COUNT|BATCH_SUM|BATCH_AVG|BATCH_MIN|BATCH_MAX|BATCH_FIRST|BATCH_LAST)\((?:\{([A-Za-z_][A-Za-z0-9_]*)\})?\)$/;
const CONCAT_RE = /^CONCAT\((.+)\)$/s;
const IF_RE = /^IF\((.+)\)$/s;
const FIELD_REF_RE = /^\{([A-Za-z_][A-Za-z0-9_]*)\}$/;
const STRING_LIT_RE = /^(?:"([^"]*)"|'([^']*)')$/;
const NUMBER_RE = /^-?\d+(?:\.\d+)?$/;
const ARITHMETIC_OP_RE = /^[+\-*/]$/;
/**
 * Splits `s` on commas that are not nested inside parentheses or braces.
 */
export function splitTopLevelCommas(s) {
    const parts = [];
    let depth = 0;
    let current = '';
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === '(' || ch === '{') {
            depth++;
            current += ch;
        }
        else if (ch === ')' || ch === '}') {
            depth--;
            current += ch;
        }
        else if (ch === ',' && depth === 0) {
            parts.push(current);
            current = '';
        }
        else {
            current += ch;
        }
    }
    parts.push(current);
    return parts;
}
function parseConcatSegment(raw) {
    const trimmed = raw.trim();
    const fieldMatch = trimmed.match(FIELD_REF_RE);
    if (fieldMatch && fieldMatch[1] !== undefined) {
        return { type: 'field', name: fieldMatch[1] };
    }
    const strMatch = trimmed.match(STRING_LIT_RE);
    if (strMatch) {
        const value = strMatch[1] !== undefined ? strMatch[1] : strMatch[2] !== undefined ? strMatch[2] : '';
        return { type: 'literal', value };
    }
    return null;
}
function tryParseArithmetic(source) {
    // Tokenize: split on operators while preserving them, respecting braces
    const tokens = [];
    let current = '';
    for (let i = 0; i < source.length; i++) {
        const ch = source[i];
        if (ch === '{') {
            // consume until closing brace
            let ref = '{';
            i++;
            while (i < source.length && source[i] !== '}') {
                ref += source[i];
                i++;
            }
            ref += '}';
            current += ref;
        }
        else if ((ch === '+' || ch === '-' || ch === '*' || ch === '/') && current.trim().length > 0) {
            tokens.push(current.trim());
            tokens.push(ch);
            current = '';
        }
        else {
            current += ch;
        }
    }
    if (current.trim().length > 0) {
        tokens.push(current.trim());
    }
    // Must be odd number of tokens (operand op operand op ...) with at least 3
    if (tokens.length < 3 || tokens.length % 2 !== 1) {
        return null;
    }
    const parts = [];
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token === undefined)
            return null;
        if (i % 2 === 0) {
            // operand: field ref or number
            const fieldMatch = token.match(FIELD_REF_RE);
            if (fieldMatch && fieldMatch[1] !== undefined) {
                parts.push({ type: 'field', name: fieldMatch[1] });
            }
            else if (NUMBER_RE.test(token)) {
                parts.push({ type: 'literal', value: token });
            }
            else {
                return null;
            }
        }
        else {
            // operator
            if (!ARITHMETIC_OP_RE.test(token))
                return null;
            parts.push({ type: 'operator', op: token });
        }
    }
    return { kind: 'arithmetic', parts };
}
/**
 * Parses a formula source string into a FormulaTemplate, or returns null
 * if the expression doesn't match any known template kind.
 */
export function parseFormulaTemplate(source) {
    const trimmed = source.trim();
    // Static values
    if (STATIC_RE.test(trimmed)) {
        return { kind: 'static', value: trimmed };
    }
    // Batch aggregation
    const batchMatch = trimmed.match(BATCH_RE);
    if (batchMatch && batchMatch[1] !== undefined) {
        return {
            kind: 'batch-aggregation',
            fn: batchMatch[1],
            fieldRef: batchMatch[2] ?? '',
        };
    }
    // Concatenation
    const concatMatch = trimmed.match(CONCAT_RE);
    if (concatMatch && concatMatch[1] !== undefined) {
        const rawSegments = splitTopLevelCommas(concatMatch[1]);
        const segments = [];
        for (const raw of rawSegments) {
            const seg = parseConcatSegment(raw);
            if (seg === null)
                return null;
            segments.push(seg);
        }
        return { kind: 'concatenation', segments };
    }
    // Conditional IF
    const ifMatch = trimmed.match(IF_RE);
    if (ifMatch && ifMatch[1] !== undefined) {
        const ifParts = splitTopLevelCommas(ifMatch[1]);
        if (ifParts.length === 3) {
            const condition = ifParts[0]?.trim();
            const thenVal = ifParts[1]?.trim();
            const elseVal = ifParts[2]?.trim();
            if (condition !== undefined && thenVal !== undefined && elseVal !== undefined) {
                return {
                    kind: 'conditional',
                    clauses: [{ condition, value: thenVal }],
                    fallback: elseVal,
                };
            }
        }
        return null;
    }
    // function-call template (Approach B: at most one nested call level)
    const fnMatch = source.trim().match(/^([A-Z][A-Z0-9_]*)\((.*)\)$/s);
    if (fnMatch && fnMatch[1] !== undefined && fnMatch[2] !== undefined) {
        const fn = fnMatch[1];
        const rawArgs = fnMatch[2].trim();
        const parts = rawArgs === '' ? [] : splitTopLevelCommas(rawArgs).map((p) => p.trim());
        const parseLeaf = (s) => {
            const f = s.match(/^\{([A-Za-z_][A-Za-z0-9_]*)\}$/);
            if (f && f[1] !== undefined)
                return { type: 'field', name: f[1] };
            if (/^("[^"]*"|'[^']*'|-?\d+(\.\d+)?)$/.test(s))
                return { type: 'literal', value: s };
            if (/^[^(){}+\-*/<>=!&|]+$/.test(s))
                return { type: 'literal', value: s };
            return null;
        };
        const args = [];
        for (const p of parts) {
            const leaf = parseLeaf(p);
            if (leaf) {
                args.push(leaf);
                continue;
            }
            const nested = p.match(/^([A-Z][A-Z0-9_]*)\(([^()]*)\)$/);
            if (nested && nested[1] !== undefined && nested[2] !== undefined) {
                const innerRaw = nested[2];
                const innerParts = innerRaw.trim() === ''
                    ? []
                    : splitTopLevelCommas(innerRaw).map((x) => x.trim());
                const innerLeaves = innerParts.map(parseLeaf);
                if (innerLeaves.every((l) => l !== null)) {
                    args.push({ type: 'call', fn: nested[1], args: innerLeaves });
                    continue;
                }
            }
            return null; // unparseable arg (operators / >=2 deep) -> degrade
        }
        return { kind: 'function', fn, args };
    }
    // Arithmetic (try last — it's the most permissive)
    const arith = tryParseArithmetic(trimmed);
    if (arith)
        return arith;
    return null;
}
/**
 * Converts a FormulaTemplate back to its source string representation.
 * Round-trips with parseFormulaTemplate.
 */
export function formulaTemplateToSource(template) {
    switch (template.kind) {
        case 'static':
            return template.value;
        case 'batch-aggregation':
            if (template.fieldRef) {
                return `${template.fn}({${template.fieldRef}})`;
            }
            return `${template.fn}()`;
        case 'concatenation':
            return `CONCAT(${template.segments
                .map((s) => {
                if (s.type === 'field')
                    return `{${s.name}}`;
                return `"${s.value}"`;
            })
                .join(', ')})`;
        case 'conditional':
            return `IF(${template.clauses
                .map((c) => `${c.condition}, ${c.value}`)
                .join(', ')}, ${template.fallback})`;
        case 'arithmetic':
            return template.parts
                .map((p) => {
                if (p.type === 'field')
                    return `{${p.name}}`;
                if (p.type === 'operator')
                    return p.op;
                return p.value;
            })
                .join(' ');
        case 'function': {
            const argToStr = (a) => {
                if (a.type === 'field')
                    return `{${a.name}}`;
                if (a.type === 'literal')
                    return a.value;
                // one nested call (leaf args only)
                const inner = a.args
                    .map((l) => (l.type === 'field' ? `{${l.name}}` : l.value))
                    .join(', ');
                return `${a.fn}(${inner})`;
            };
            return `${template.fn}(${template.args.map(argToStr).join(', ')})`;
        }
    }
}
// ── Dependency extraction ───────────────────────────────────────────
const BRACED_REF = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
/**
 * Scans `source` for `{FieldName}` patterns and returns a sorted,
 * deduplicated list of field names. Dotted refs like `lineItems.amount`
 * don't match the braced pattern and are ignored.
 */
export function extractDependencies(source) {
    const refs = new Set();
    let match;
    while ((match = BRACED_REF.exec(source)) !== null) {
        const name = match[1];
        if (name !== undefined) {
            refs.add(name);
        }
    }
    return [...refs].sort();
}
//# sourceMappingURL=formula-templates.js.map